import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RBHBox, RBHButton, RBHText, RBHTextInput, TagCount } from '../../../shared/components';

import styleText from '../../../shared/components/rbh-text/styles';
import { Row } from 'antd';
import { textStyles } from '../../../shared/theme/fonts-ppv';
import { Colors } from '../../../shared/theme';
import { EditOutlined, ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import RBHTable2 from '../../../shared/components/rbh-table-2';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { modifierGroupCreators } from './redux/modifier-group-action';
import { MODIFIER_GROUP_KEY, modifierGroupStateSelector } from './redux/modifier-group-reducer';
import { ModifierGroup, ModifierGroupType } from './redux/modifier-group-type';
import { ModifierItem } from '../ModifierItem/redux/modifier-item-type';
import AddButton from '../components/Button/AddButton';
import images from '../../../shared/theme/images';
import { httpCreators } from '@ppvrbh/merchant-frontend-core';
import { MODIFIER_GROUP_REQUEST_ID_LIST } from './redux/modifier-group-constant';
import { modifierGroupSagas } from './redux/modifier-group-saga';
import { ModifierGroupService } from './redux/modifier-group-service';
import { RBHList } from '../../../shared/components/rbh-list';
import { generalCreators } from '../../../redux/general/general-action';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../redux/general/general-reducer';
import { MODAL_TYPE } from '../../../redux/general/general-constant';
import { DEBOUNCE_TIMEOUT } from '../../../shared/helpers/constant';
import { SortButton } from '../components/SortButton';
import metrics from '../../../shared/theme/metrics';
import { reportCreators } from '../Report/redux/report-action';

const ModifierGroupScreen = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const modifierGroups = useSelector(modifierGroupStateSelector(MODIFIER_GROUP_KEY.MODIFIER_GROUPS));
    const meta = useSelector(modifierGroupStateSelector(MODIFIER_GROUP_KEY.META));
    const totalGroups = useSelector(modifierGroupStateSelector(MODIFIER_GROUP_KEY.TOTAL_GROUPS)) ?? 0;
    
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [searchKeyword, setSearchKeyword] = useState('');
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const sortedItemRef = useRef<{ id: number; name: string }[]>([]);
     
    const appConfig = useSelector(generalStateSelector(GENERAL_STATE_KEY.REMOTE_CONFIG));
    const maxGroup = Number(appConfig?.find(item => item.key === 'modifier.group.maximum')?.value) || 0;

    const tableData = (modifierGroups ?? []).map((item: ModifierGroup) => ({
        ...item,
        id: item.menuModifierGroupId,
    }));

    const loadData = (page = currentPage, limit = pageSize, keyword = debouncedKeyword) => {
        dispatch(modifierGroupCreators.callModifierGroups({ page, limit, keyword }));
    };

    useEffect(() => {
        if (!appConfig) {
            dispatch(reportCreators.callConfigAndShopList(() => {}, () => {}));
        }
    }, [])

    useEffect(() => {
        loadData(currentPage, pageSize, debouncedKeyword);
    }, [currentPage, pageSize, debouncedKeyword]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedKeyword(searchKeyword);
            setCurrentPage(1);
        }, DEBOUNCE_TIMEOUT);
        return () => clearTimeout(timer);
    }, [searchKeyword]);

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(e.target.value);
    };

    const openSortModal = (groups: ModifierGroup[]) => {
        const sortableItems = groups.map((g) => ({
            id: g.menuModifierGroupId,
            name: g.menuModifierGroupName,
        }));
        sortedItemRef.current = sortableItems;

        const handleSubmit = () => {
            dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, { visible: false }));
            const sequenceList = sortedItemRef.current.map((item, index) => ({
                menuModifierGroupId: item.id,
                menuModifierGroupSequence: index + 1,
            }));
            dispatch(httpCreators.request({
                id: MODIFIER_GROUP_REQUEST_ID_LIST.UPDATE_MODIFIER_GROUP_SEQUENCE,
                sagaHandler: modifierGroupSagas.updateModifierGroupSequenceHandler,
                callbackParams: () => {
                    dispatch(modifierGroupCreators.setModifierGroupState(MODIFIER_GROUP_KEY.ALL_MODIFIER_GROUPS, undefined));
                    loadData();
                },
                service: ModifierGroupService.updateSequenceMenuModifierGroup,
                serviceParams: { modifierGroups: sequenceList },
            }));
        };

        dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
            visible: true,
            type: MODAL_TYPE.LIST_SORT_FORM,
            modalTitle: 'main.menu.modifiergroup.sort.modal.title',
            modalContent: (
                <RBHList
                    dragable
                    dataSource={sortableItems}
                    renderItem={(item) => <span>{item.name}</span>}
                    onSort={(items) => { sortedItemRef.current = items as { id: number; name: string }[]; }}
                />
            ),
            onOk: handleSubmit,
            onCancel: () => dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, { visible: false })),
        }));
    };

    const onSortButtonClick = () => {
        dispatch(modifierGroupCreators.callAllModifierGroups((groups) => {
            openSortModal(groups);
        }));
    };

    const isMaxGroupReached = useMemo(() => {
        if (!maxGroup || totalGroups === undefined) return false;
        return totalGroups >= maxGroup;
    }, [totalGroups, maxGroup])
    
    return (
        <RBHBox>
            {/* Header */}
            <RBHBox style={{ borderBottom: '1px solid #FBFBFD', paddingBottom: '16px', marginBottom: '16px' }}>
                <RBHText style={styleText.headerText} text={'main.menu.modifiergroup.title'} />
                <RBHText style={styleText.subTitleText} text={'main.menu.modifiergroup.subtitle'} />
            </RBHBox>
            {/* Content */}
            <RBHBox style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Row justify='space-between' align='middle' style={{ gap: 8 }}>
                    <Row  align='middle' style={{ gap: 8 }}>
                        <RBHText style={{ ...textStyles.h4, color: Colors.grey800 }} text={'main.menu.modifiergroup.total.title'} />
                        <TagCount id="modifier-group-total-count" count={totalGroups ?? 0} maximum={maxGroup} />
                        <RBHTextInput
                            prefix={<SearchOutlined style={{ color: Colors.grey600 }} translate={null} />}
                            placeholder='main.menu.modifiergroup.search.placeholder'
                            id={'search-modifierGroup-input'}
                            style={{ width: '240px', borderColor: Colors.grey100, borderRadius: '8px' }}
                            value={searchKeyword}
                            onChange={onSearchChange}
                        />
                    </Row>

                    <Row align='middle' style={{ gap: 8 }}>
                        <SortButton id="modifier-group-detail-sort" disable={totalGroups < 2} textId='main.menu.modifiergroup.detail.sort' onClick={onSortButtonClick}/>
                        {isMaxGroupReached &&
                            <Row align='middle' style={{ gap: 4 }}>
                                <ExclamationCircleOutlined translate={null} style={{ color: Colors.red600, fontSize: 16 }} />
                                <RBHText style={{ ...textStyles.subtitle2, color: Colors.red600 }} text={'main.menu.modifieritem.max.reached'} />
                            </Row>
                        }
                        <AddButton 
                            id={'add-modifierGroup-button'} 
                            text={'main.menu.modifiergroup.add.button'} 
                            onClick={() => history.push('/main/modifier-group/add')} 
                            disabled={isMaxGroupReached} />
                    </Row>
                </Row>
                <RBHTable2
                    dataSource={tableData}
                    scroll={{ y: 'calc(100vh - 300px)', x: 'auto' }}
                    pagination={{
                        showSizeChanger: true,
                        current: currentPage,
                        pageSize: pageSize,
                        total: meta?.totalItems ?? 0,
                    }}
                    onChange={(value) => {
                        if (value.current) setCurrentPage(value.current);
                        if (value.pageSize) setPageSize(value.pageSize);
                    }}
                    columns={[
                        { title: <FormattedMessage id="main.menu.modifiergroup.column.name" />, dataIndex: 'menuModifierGroupName', width: metrics.tableHeader.width200 },
                        { title: <FormattedMessage id="main.menu.modifiergroup.column.nameEN" />, dataIndex: 'menuModifierGroupNameEN', width: metrics.tableHeader.width200 },
                        { title: <FormattedMessage id="main.menu.modifiergroup.column.total" />, dataIndex: 'menuModifierItems', render: (items: ModifierItem[]) => items?.length ?? 0, width: metrics.tableHeader.width180 },
                        {
                            title: <FormattedMessage id="main.menu.modifiergroup.column.condition" />,
                            dataIndex: 'menuModifierGroupType',
                            render: (type: ModifierGroupType) => {
                                const labelMap: Record<ModifierGroupType, string> = {
                                    [ModifierGroupType.OPTIONAL]: 'main.menu.modifiergroup.type.optional',
                                    [ModifierGroupType.SELECT_ONE]: 'main.menu.modifiergroup.type.select_one',
                                    [ModifierGroupType.MULTIPLE]: 'main.menu.modifiergroup.type.multiple',
                                };
                                const isOptional = type === ModifierGroupType.OPTIONAL;
                                return (
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '4px 6px',
                                        borderRadius: '24px',
                                        ...textStyles.body3,
                                        background: isOptional ? Colors.grey100 : Colors.yellow100,
                                        color: isOptional ? Colors.grey800 : Colors.yellow800,
                                    }}>
                                        <FormattedMessage id={labelMap[type]} />
                                    </span>
                                );
                            },
                            width: metrics.tableHeader.width180
                        },
                        {
                            title: 'Action',
                            align: 'center',
                            width: metrics.tableHeader.width120,
                            render: (_, record: ModifierGroup) => (
                                <EditOutlined
                                    id={`edit-${record.menuModifierGroupId}`}
                                    translate={null}
                                    style={{ color: Colors.grey600, fontSize: 18 }}
                                    onClick={() => history.push(`/main/modifier-group/${record.menuModifierGroupId}`, { group: record })}
                                />
                            ),
                        },
                    ]}
                />
            </RBHBox>
        </RBHBox>
    );
};

export default ModifierGroupScreen;
